import { deleteOrderFromBook, insertOrderForBook, isReqError } from '../../services/utils';
import { sortTickDataWithBook } from '../../services/sortUtils';
import { IPublicOrderByLevel, IPublicOrderHistoryEntry, IPublicTradeForGroup, IPublicTradeHistoryEntry, PublicOrderByLevelTickData, PublicOrderTickData, PublicTradeTickData } from '../../types/api/PublicTypes';
import {
    WithBook,
    Requested,
    BySymbolByTradingDay,
    changeBySymbolByTradingDayState,
    ByTradingDay,
    BySymbolByDateRange,
    changeBySymbolByDateRangeState
} from '../../types/api/Types';
import {
    PublicActions,
    REQUEST_PUBLIC_ORDERS_WAITING,
    REQUEST_PUBLIC_ORDERS_SUCCESS,
    REQUEST_PUBLIC_ORDERS_FAILURE,
    REQUEST_PUBLIC_TRADES_WAITING,
    REQUEST_PUBLIC_TRADES_SUCCESS,
    REQUEST_PUBLIC_TRADES_FAILURE,
    REQUEST_PUBLIC_TRADES_FOR_GROUP_WAITING,
    REQUEST_PUBLIC_TRADES_FOR_GROUP_SUCCESS,
    REQUEST_PUBLIC_TRADES_FOR_GROUP_FAILURE,
    REQUEST_LAST_PRICE_WAITING,
    REQUEST_LAST_PRICE_SUCCESS,
    REQUEST_LAST_PRICE_FAILURE,
    REQUEST_PREV_TRADING_DAY_WAITING,
    REQUEST_PREV_TRADING_DAY_SUCCESS,
    REQUEST_PREV_TRADING_DAY_FAILURE,
    REQUEST_PUBLIC_ORDERS_BY_LEVEL_WAITING,
    REQUEST_PUBLIC_ORDERS_BY_LEVEL_SUCCESS,
    REQUEST_PUBLIC_ORDERS_BY_LEVEL_FAILURE,
    REQUEST_OPEN_CLOSING_PRICE_WAITING,
    REQUEST_OPEN_CLOSING_PRICE_SUCCESS,
    REQUEST_OPEN_CLOSING_PRICE_FAILURE
} from '../../types/redux/PublicTypes';
import { OpenClosingPrice } from '../../types/api/OpenClosingPrice';

export interface IPublicState {
    publicOpenClosingPrice: BySymbolByDateRange<Requested<OpenClosingPrice[]>>,
    publicOrderDoubleMap: BySymbolByTradingDay<
        Requested<
            Array<WithBook<PublicOrderTickData>>
        >
    >,
    publicTradeDoubleMap: BySymbolByTradingDay<
        Requested<
            Array<PublicTradeTickData>
        >
    >,
    publicTradesForGroupByTradingDay: ByTradingDay<
        Requested<
            Array<IPublicTradeForGroup>
        >
    >,
    lastPriceDoubleMap: BySymbolByTradingDay<
        Requested<{
            lastPrice: number | null,
            // Keep track of previous trading day. That's how change % is calculated.
            previousTradingDay: Requested<string | null>
        }>
    >,
    publicOrderByLevelDoubleMap: BySymbolByTradingDay<
        Requested<
            Array<WithBook<PublicOrderByLevelTickData>>
        >
    >,
}

const initialState: IPublicState = {
    publicOpenClosingPrice: {},
    publicOrderDoubleMap: {},
    publicTradeDoubleMap: {},
    publicTradesForGroupByTradingDay: {},
    lastPriceDoubleMap: {},
    publicOrderByLevelDoubleMap: {}
}

export default function(state: IPublicState = initialState, action: PublicActions): IPublicState {
    switch(action.type) {
        // REQUEST PUBLIC ORDERS
        case REQUEST_OPEN_CLOSING_PRICE_WAITING: {
            const { symbol, fromDate, toDate } = action.payload;
            const newPublicOpenClosingPrice = changeBySymbolByDateRangeState(state.publicOpenClosingPrice, symbol, fromDate, toDate, 'waiting');
            const newState: IPublicState = {
              ...state,
              publicOpenClosingPrice: newPublicOpenClosingPrice,
            };
            return newState;
        }
        case REQUEST_OPEN_CLOSING_PRICE_SUCCESS: {
            const { symbol, fromDate, toDate, openClosingPrice } = action.payload;
            const newPublicOpenClosingPrice = changeBySymbolByDateRangeState(state.publicOpenClosingPrice, symbol, fromDate, toDate, openClosingPrice);
            const newState: IPublicState = {
              ...state,
              publicOpenClosingPrice: newPublicOpenClosingPrice,
            };
            return newState;
        }
        case REQUEST_OPEN_CLOSING_PRICE_FAILURE: {
            const { symbol, fromDate, toDate, error } = action.payload;
            const newPublicOpenClosingPrice = changeBySymbolByDateRangeState(state.publicOpenClosingPrice, symbol, fromDate, toDate, { message: error });
            const newState: IPublicState = {
              ...state,
              publicOpenClosingPrice: newPublicOpenClosingPrice,
            };
            return newState;
        }

        // REQUEST PUBLIC ORDERS.
        case REQUEST_PUBLIC_ORDERS_WAITING:
        {
            const { symbol, tradingDay } = action.payload;
            const newPublicOrderDoubleMap = changeBySymbolByTradingDayState(state.publicOrderDoubleMap, symbol, tradingDay, 'waiting');
            const newState: IPublicState = {
                ...state,
                publicOrderDoubleMap: newPublicOrderDoubleMap
            };
            return newState;
        }
        case REQUEST_PUBLIC_ORDERS_SUCCESS:
        {
            const { symbol, tradingDay, orderHistory } = action.payload;
            const orderTickBookData = orderHistoryToTickDataWithBook(orderHistory);
            const newPublicOrderDoubleMap = changeBySymbolByTradingDayState(state.publicOrderDoubleMap, symbol, tradingDay, orderTickBookData);
            const newState: IPublicState = {
                ...state,
                publicOrderDoubleMap: newPublicOrderDoubleMap
            };
            return newState;
        }
        case REQUEST_PUBLIC_ORDERS_FAILURE:
        {
            const { symbol, tradingDay, error } = action.payload;
            const newPublicOrderDoubleMap = changeBySymbolByTradingDayState(state.publicOrderDoubleMap, symbol, tradingDay, { message: error });
            const newState: IPublicState = {
                ...state,
                publicOrderDoubleMap: newPublicOrderDoubleMap
            };
            return newState;
        }

        // REQUEST PUBLIC TRADES
        case REQUEST_PUBLIC_TRADES_WAITING: {
            const { symbol, tradingDay } = action.payload;
            const newPublicTradeDoubleMap = changeBySymbolByTradingDayState(state.publicTradeDoubleMap, symbol, tradingDay, 'waiting');
            const newState: IPublicState = {
                ...state,
                publicTradeDoubleMap: newPublicTradeDoubleMap
            };
            return newState;
        }
        case REQUEST_PUBLIC_TRADES_SUCCESS: {
            const { symbol, tradingDay, tradeHistory } = action.payload;
            const tradeTickData = tradeHistoryToTickData(tradeHistory);
            const newPublicTradeDoubleMap = changeBySymbolByTradingDayState(state.publicTradeDoubleMap, symbol, tradingDay, tradeTickData);
            const newState: IPublicState = {
                ...state,
                publicTradeDoubleMap: newPublicTradeDoubleMap
            };
            return newState;
        }
        case REQUEST_PUBLIC_TRADES_FAILURE: {
            const { symbol, tradingDay, error } = action.payload;
            const newPublicTradeDoubleMap = changeBySymbolByTradingDayState(state.publicTradeDoubleMap, symbol, tradingDay, { message: error });
            const newState: IPublicState = {
                ...state,
                publicTradeDoubleMap: newPublicTradeDoubleMap
            };
            return newState;
        }

        // REQUEST PUBLIC TRADES FOR GROUP
        case REQUEST_PUBLIC_TRADES_FOR_GROUP_WAITING: {
            const { tradingDay } = action.payload;
            const publicTradesForGroupByTradingDayCopy = { ...state.publicTradesForGroupByTradingDay };
            publicTradesForGroupByTradingDayCopy[tradingDay] = 'waiting';
            const newState: IPublicState = {
                ...state,
                publicTradesForGroupByTradingDay: publicTradesForGroupByTradingDayCopy
            };
            return newState;
        }
        case REQUEST_PUBLIC_TRADES_FOR_GROUP_SUCCESS: {
            const { tradingDay, publicGroupTrades } = action.payload;
            const publicTradesForGroupByTradingDayCopy = { ...state.publicTradesForGroupByTradingDay };
            publicTradesForGroupByTradingDayCopy[tradingDay] = publicGroupTrades;
            const newState: IPublicState = {
                ...state,
                publicTradesForGroupByTradingDay: publicTradesForGroupByTradingDayCopy
            };
            return newState;
        }
        case REQUEST_PUBLIC_TRADES_FOR_GROUP_FAILURE: {
            const { tradingDay, error } = action.payload;
            const publicTradesForGroupByTradingDayCopy = { ...state.publicTradesForGroupByTradingDay };
            publicTradesForGroupByTradingDayCopy[tradingDay] = { message: error };
            const newState: IPublicState = {
                ...state,
                publicTradesForGroupByTradingDay: publicTradesForGroupByTradingDayCopy
            };
            return newState;
        }

        // REQUEST LAST PRICE.
        case REQUEST_LAST_PRICE_WAITING:
        {
            const { symbol, tradingDay } = action.payload;
            const newLastPriceDoubleMap = changeBySymbolByTradingDayState(state.lastPriceDoubleMap, symbol, tradingDay, 'waiting');

            const newState: IPublicState = {
                ...state,
                lastPriceDoubleMap: newLastPriceDoubleMap
            };
            return newState;
        }
        case REQUEST_LAST_PRICE_SUCCESS:
        {
            const { symbol, tradingDay, lastPrice } = action.payload;
            const newLastPriceDoubleMap = changeBySymbolByTradingDayState(state.lastPriceDoubleMap, symbol, tradingDay, {
                lastPrice,
                previousTradingDay: undefined
            });

            const newState: IPublicState = {
                ...state,
                lastPriceDoubleMap: newLastPriceDoubleMap
            };
            return newState;
        }
        case REQUEST_LAST_PRICE_FAILURE:
        {
            const { symbol, tradingDay, error } = action.payload;
            const newLastPriceDoubleMap = changeBySymbolByTradingDayState(state.lastPriceDoubleMap, symbol, tradingDay, { message: error });

            const newState: IPublicState = {
                ...state,
                lastPriceDoubleMap: newLastPriceDoubleMap
            };
            return newState;
        }
        // REQUEST PREVIOUS TRADING DAY.
        case REQUEST_PREV_TRADING_DAY_WAITING:
        {
            const { symbol, tradingDay } = action.payload;
            const currentLastPriceDoubleMapEntry = state.lastPriceDoubleMap?.[symbol]?.[tradingDay];
            if (
                currentLastPriceDoubleMapEntry === undefined
                || currentLastPriceDoubleMapEntry === 'waiting'
                || isReqError(currentLastPriceDoubleMapEntry)
            ) {
                return state;
            }
            const newLastPriceDoubleMap = changeBySymbolByTradingDayState(state.lastPriceDoubleMap, symbol, tradingDay, {
                ...currentLastPriceDoubleMapEntry,
                previousTradingDay: 'waiting'
            });

            const newState: IPublicState = {
                ...state,
                lastPriceDoubleMap: newLastPriceDoubleMap
            };
            return newState;
        }
        case REQUEST_PREV_TRADING_DAY_SUCCESS:
        {
            const { symbol, tradingDay, prevTradingDay } = action.payload;
            const currentLastPriceDoubleMapEntry = state.lastPriceDoubleMap?.[symbol]?.[tradingDay];
            if (
                currentLastPriceDoubleMapEntry === undefined
                || currentLastPriceDoubleMapEntry === 'waiting'
                || isReqError(currentLastPriceDoubleMapEntry)
            ) {
                return state;
            }
            const newLastPriceDoubleMap = changeBySymbolByTradingDayState(state.lastPriceDoubleMap, symbol, tradingDay, {
                ...currentLastPriceDoubleMapEntry,
                previousTradingDay: prevTradingDay
            });

            const newState: IPublicState = {
                ...state,
                lastPriceDoubleMap: newLastPriceDoubleMap
            };
            return newState;
        }
        case REQUEST_PREV_TRADING_DAY_FAILURE:
        {
            const { symbol, tradingDay, error } = action.payload;
            const currentLastPriceDoubleMapEntry = state.lastPriceDoubleMap?.[symbol]?.[tradingDay];
            if (
                currentLastPriceDoubleMapEntry === undefined
                || currentLastPriceDoubleMapEntry === 'waiting'
                || isReqError(currentLastPriceDoubleMapEntry)
            ) {
                return state;
            }
            const newLastPriceDoubleMap = changeBySymbolByTradingDayState(state.lastPriceDoubleMap, symbol, tradingDay, {
                ...currentLastPriceDoubleMapEntry,
                previousTradingDay: { message: error }
            });

            const newState: IPublicState = {
                ...state,
                lastPriceDoubleMap: newLastPriceDoubleMap
            };
            return newState;
        }
        // PUBLIC ORDERS BY LEVEL
        case REQUEST_PUBLIC_ORDERS_BY_LEVEL_WAITING: {
            const { symbol, tradingDay } = action.payload;
            const newPublicOrderByLevelDoubleMap = changeBySymbolByTradingDayState(state.publicOrderByLevelDoubleMap, symbol, tradingDay, 'waiting');
            const newState: IPublicState = {
                ...state,
                publicOrderByLevelDoubleMap: newPublicOrderByLevelDoubleMap
            };
            return newState;
        }
        case REQUEST_PUBLIC_ORDERS_BY_LEVEL_SUCCESS: {
            const { symbol, tradingDay, publicOrdersByLevel } = action.payload;
            const orderTickBookData = publicOrdersByLevelToTickDataWithBook(publicOrdersByLevel);
            const newPublicOrderByLevelDoubleMap = changeBySymbolByTradingDayState(state.publicOrderByLevelDoubleMap, symbol, tradingDay, orderTickBookData);
            const newState: IPublicState = {
                ...state,
                publicOrderByLevelDoubleMap: newPublicOrderByLevelDoubleMap
            };
            return newState;
        }
        case REQUEST_PUBLIC_ORDERS_BY_LEVEL_FAILURE: {
            const { symbol, tradingDay, error } = action.payload;
            const newPublicOrderByLevelDoubleMap = changeBySymbolByTradingDayState(state.publicOrderByLevelDoubleMap, symbol, tradingDay, { message: error });
            const newState: IPublicState = {
                ...state,
                publicOrderByLevelDoubleMap: newPublicOrderByLevelDoubleMap
            };
            return newState;
        }
        // DEFAULT
        default: {
            return state;
        }
    }
}

// HELPER FUNCTIONS.

const orderHistoryToTickDataWithBook = (orderHistory: Array<IPublicOrderHistoryEntry>): Array<WithBook<PublicOrderTickData>> => {
    const orderHistoryTickDataBookObjects: Array<WithBook<PublicOrderTickData>> = orderHistory.map(orderHistoryEntry => ({
        tick: {
            ...orderHistoryEntry,
            tickdata_type: 'Order',
            volume: orderHistoryEntry.quantity,
            timestamp: (orderHistoryEntry.update_code !== 'Deleted') // if
                ? orderHistoryEntry.order_timestamp_utc
                : (orderHistoryEntry.deleted_timestamp_utc !== null) // else if
                ? orderHistoryEntry.deleted_timestamp_utc
                : orderHistoryEntry.order_timestamp_utc
        },
        book: {
            bookAskPrice: null,
            bookAskQuantity: null,
            bookBidPrice: null,
            bookBidQuantity: null
        }
    }));

    const sortedOrderHistoryTickData = sortTickDataWithBook(orderHistoryTickDataBookObjects);

    let bidOrdersInPriorityOrder: PublicOrderTickData[] = [];
    let askOrdersInPriorityOrder: PublicOrderTickData[] = [];

    for (let bookTickDataEntry of sortedOrderHistoryTickData) {
        const { tick } = bookTickDataEntry;
        const { side } = tick;
        // Insert.
        if (tick.update_code === 'Insert') {
            if (side === 'bid') {
                bidOrdersInPriorityOrder = insertOrderForBook(tick, bidOrdersInPriorityOrder, true);
            } else {
                askOrdersInPriorityOrder = insertOrderForBook(tick, askOrdersInPriorityOrder, false);
            }
        }
        // Update.
        else if (tick.update_code === 'Update') {
            if (side === 'bid') {
                bidOrdersInPriorityOrder = deleteOrderFromBook(tick, bidOrdersInPriorityOrder);
                bidOrdersInPriorityOrder = insertOrderForBook(tick, bidOrdersInPriorityOrder, true);
            } else {
                askOrdersInPriorityOrder = deleteOrderFromBook(tick, askOrdersInPriorityOrder);
                askOrdersInPriorityOrder = insertOrderForBook(tick, askOrdersInPriorityOrder, false);
            }
        }
        // Delete.
        else {
            if (side === 'bid') {
                bidOrdersInPriorityOrder = deleteOrderFromBook(tick, bidOrdersInPriorityOrder);
            } else {
                askOrdersInPriorityOrder = deleteOrderFromBook(tick, askOrdersInPriorityOrder);
            }
        }
        // Add current top of book to orderHistoryEntries.
        const topAsk: PublicOrderTickData | null = askOrdersInPriorityOrder[0] ?? null;
        const topBid: PublicOrderTickData | null = bidOrdersInPriorityOrder[0] ?? null;
        bookTickDataEntry.book = {
            bookAskPrice: topAsk?.price ?? null,
            bookAskQuantity: topAsk?.volume ?? null,
            bookBidPrice: topBid?.price ?? null,
            bookBidQuantity: topBid?.volume ?? null,
        };
    }

    return orderHistoryTickDataBookObjects;
}


const publicOrdersByLevelToTickDataWithBook = (publicOrdersByLevel: Array<IPublicOrderByLevel>): Array<WithBook<PublicOrderByLevelTickData>> => {
    const publicOrdersByLevelTickData: Array<WithBook<PublicOrderByLevelTickData>> = publicOrdersByLevel.map(publicOrderByLevel => ({
        tick: {
            ...publicOrderByLevel,
            tickdata_type: 'Order By Level',
            volume: publicOrderByLevel.quantity,
            timestamp: publicOrderByLevel.time_exec_utc
        },
        book: {
            bookAskPrice: null,
            bookAskQuantity: null,
            bookBidPrice: null,
            bookBidQuantity: null
        }
    }));

    const sortedPublicOrdersByLevelTickData = sortTickDataWithBook(publicOrdersByLevelTickData);

    let currentBid: PublicOrderByLevelTickData | null = null;
    let currentAsk: PublicOrderByLevelTickData | null = null;

    for (let publicOrderByLevelTick of sortedPublicOrdersByLevelTickData) {
        if (publicOrderByLevelTick.tick.level !== 1) continue;
        const { tick } = publicOrderByLevelTick;
        const { side, update_code } = tick;
        if (update_code === 'Update') {
            if (side === 'bid') currentBid = tick;
            else currentAsk = tick;
        } else {
            if (side === 'bid') currentBid = null;
            else currentAsk = null;
        }
        // Add current top of book to orderHistoryEntries.
        publicOrderByLevelTick.book = {
            bookAskPrice: currentAsk?.price || null,
            bookAskQuantity: currentAsk?.volume || null,
            bookBidPrice: currentBid?.price || null,
            bookBidQuantity: currentBid?.volume || null,
        };
    }

    return sortedPublicOrdersByLevelTickData;
}

const tradeHistoryToTickData = (tradeHistory: Array<IPublicTradeHistoryEntry>): Array<PublicTradeTickData> => {
    const tradeHistoryTickData: Array<PublicTradeTickData> = tradeHistory.map(tradeHistoryEntry => ({
        ...tradeHistoryEntry,
        tickdata_type: 'Trade',
        update_code: tradeHistoryEntry.trade_class ?? '',
        timestamp: tradeHistoryEntry.time_exec_utc
    }));
    return tradeHistoryTickData;
}